import React, { useState, useEffect } from "react";
import {
  Box,
  Badge,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Typography,
} from "@mui/material";
import moment from "moment";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { TherapyChip } from "../../../atoms/Chip";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { useApi } from "../../../api/ApiProvider";
import {
  DoctorReportDto,
  DoctorReportStatusDtoStatusEnum,
  GetClinicalDoctorReportsWithFiltersStatusEnum,
} from "../../../api-client";
import { therapyRxTermLabel } from "../../../helpers/therapyRxLabel";
import { termColorKey } from "../../../helpers/slotTherapy";

/** Maps legacy status values to new display values */
const mapDoctorReportStatus = (status: string | undefined | null): string => {
  if (!status) return DoctorReportStatusDtoStatusEnum.Entwurf;
  const mapping: Record<string, string> = {
    "None - Requested": DoctorReportStatusDtoStatusEnum.Entwurf,
    Requested: DoctorReportStatusDtoStatusEnum.Entwurf,
    Incomplete: DoctorReportStatusDtoStatusEnum.Entwurf,
    Complete: DoctorReportStatusDtoStatusEnum.Abgegeben,
    Sent: DoctorReportStatusDtoStatusEnum.Versendet,
    Abgabefertig: DoctorReportStatusDtoStatusEnum.Abgegeben,
    Abgeschlossen: DoctorReportStatusDtoStatusEnum.Versendet,
  };
  return mapping[status] ?? status;
};

const getReportStatusColor = (status: string | undefined | null): string => {
  const mapped = mapDoctorReportStatus(status);
  switch (mapped) {
    case DoctorReportStatusDtoStatusEnum.Entwurf:
      return "#9e9e9e";
    case DoctorReportStatusDtoStatusEnum.Abgegeben:
      return "#f57c00";
    case DoctorReportStatusDtoStatusEnum.Versendet:
      return "#388e3c";
    default:
      return "#9e9e9e";
  }
};

interface ClinicDrReportsSectionProps {
  clinicBsnr: string;
}

const ClinicDrReportsSection: React.FC<ClinicDrReportsSectionProps> = ({ clinicBsnr }) => {
  const { clinicalDocumentation } = useApi();
  const [reports, setReports] = useState<DoctorReportDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clinicBsnr) return;

    const fetchReports = async () => {
      setLoading(true);
      try {
        console.log("[ClinicDrReportsSection] Fetching reports for BSNR:", clinicBsnr);
        // Fetch Entwurf reports
        const entwurfResponse = await clinicalDocumentation.getClinicalDoctorReportsWithFilters(
          50, // limit (max allowed by API)
          1, // page
          undefined, // sortOrder
          undefined, // userId
          undefined, // assignedToUserId
          undefined, // patientId
          undefined, // locationId
          GetClinicalDoctorReportsWithFiltersStatusEnum.Entwurf,
          clinicBsnr, // doctorBsnr
          undefined // accountId
        );

        // Fetch Abgegeben reports
        const abgegebenResponse = await clinicalDocumentation.getClinicalDoctorReportsWithFilters(
          50,
          1,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          GetClinicalDoctorReportsWithFiltersStatusEnum.Abgegeben,
          clinicBsnr,
          undefined
        );

        console.log("[ClinicDrReportsSection] Entwurf response:", entwurfResponse);
        console.log("[ClinicDrReportsSection] Abgegeben response:", abgegebenResponse);
        const entwurfData = (entwurfResponse as any)?.data || [];
        const abgegebenData = (abgegebenResponse as any)?.data || [];
        setReports([...entwurfData, ...abgegebenData]);
      } catch (error) {
        console.error("Error fetching doctor reports for clinic:", error);
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [clinicBsnr, clinicalDocumentation]);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Offene Therapieberichte
        </Typography>
        <Badge
          badgeContent={reports.length}
          color="warning"
          sx={{ ml: 1 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : reports.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Keine offenen Therapieberichte
        </Typography>
      ) : (
        <List sx={{ padding: 0, maxHeight: 250, overflow: "auto" }}>
          {reports.map((report: any, index) => {
            const displayStatus = mapDoctorReportStatus(report.status);
            const statusColor = getReportStatusColor(report.status);

            return (
              <ListItem key={report.id || index} sx={{ padding: "4px 8px" }}>
                <ListItemButton
                  sx={{ padding: "0px", borderRadius: "10px", width: "100%" }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      padding: "10px 12px",
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      backgroundColor: mobiliTheme.lowLevelContainerBackground,
                      gap: "6px",
                      borderRadius: "10px",
                      borderLeft: `4px solid ${statusColor}`,
                      transition: "box-shadow 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: 3,
                      },
                    }}
                  >
                    {/* Row 1: Status + Avatar */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}>
                      <Chip
                        label={displayStatus || "–"}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          minWidth: "80px",
                          height: "24px",
                          backgroundColor: statusColor,
                          color: "#fff",
                          borderRadius: "6px",
                          flexShrink: 0,
                        }}
                      />
                      <MobiliAvatar
                        user={report.assignedToUser || report.createdByUser}
                        sx={{ width: 28, height: 28, flexShrink: 0 }}
                      />
                      {/* Patient name */}
                      {report.rx?.patient && (
                        <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                          {report.rx.patient.firstName} {report.rx.patient.lastName}
                        </Typography>
                      )}
                    </Box>

                    {/* Row 2: Rx Number, Date, Therapy Chips */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", pl: "4px" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.85rem", whiteSpace: "nowrap" }}
                      >
                        {report.rx?.rxNumber}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>•</Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontSize: "0.8rem", whiteSpace: "nowrap" }}
                      >
                        {report.createdAt ? moment(report.createdAt).format("DD.MM.YYYY") : "–"}
                      </Typography>
                      {report.rx?.therapyRxs && report.rx.therapyRxs.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ color: "text.disabled" }}>•</Typography>
                          {report.rx.therapyRxs
                            .slice()
                            .sort((a: any, b: any) => a.order - b.order)
                            .map((therapyRx: any, idx: number) => (
                              <TherapyChip
                                key={idx}
                                therapyName={therapyRxTermLabel(therapyRx)}
                                colorKey={
                                  therapyRx.prescribedTherapy?.id ?? therapyRx.prescribedTherapyId
                                    ? termColorKey(
                                        therapyRx.prescribedTherapy?.id ?? therapyRx.prescribedTherapyId
                                      )
                                    : undefined
                                }
                                amount={therapyRx.amount}
                              />
                            ))}
                        </>
                      )}
                    </Box>
                  </Paper>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default ClinicDrReportsSection;
